import React, { useEffect, useState } from "react"
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Snackbar,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { Container } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import {
  getAllContactsThunk,
  deleteContactThunk,
} from "../../../features/contact/contactSlice"

const ContactUs = () => {
  const dispatch = useDispatch()
  const { contacts, loading, error } = useSelector((state) => state.contact)

  const [searchQuery, setSearchQuery] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [deleteId, setDeleteId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  useEffect(() => {
    dispatch(getAllContactsThunk())
  }, [dispatch])

  // Filtered contacts
  const filteredContacts = Array.isArray(contacts)
    ? contacts.filter((contact) => {
        const matchesSearch =
          contact.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.phone_number
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          contact.service_chosen
            .toLowerCase()
            .includes(searchQuery.toLowerCase())

        let matchesDate = true
        const contactDate = new Date(contact.created_at || contact.date)

        if (startDate)
          matchesDate = matchesDate && contactDate >= new Date(startDate)
        if (endDate) {
          const end = new Date(endDate)
          end.setHours(23, 59, 59, 999)
          matchesDate = matchesDate && contactDate <= end
        }

        return matchesSearch && matchesDate
      })
    : []

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage)
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Delete handler
  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await dispatch(deleteContactThunk(deleteId))
      setSnackbar({
        open: true,
        message: "Contact deleted successfully",
        severity: "success",
      })
      setDeleteId(null)
      dispatch(getAllContactsThunk()) // refresh table
    } catch (err) {
      console.error("Delete error:", err)
      setSnackbar({
        open: true,
        message: "Failed to delete contact",
        severity: "error",
      })
    } finally {
      setDeleting(false)
    }
  }

  const handleExportCSV = () => {
    if (!filteredContacts || filteredContacts.length === 0) return

    // Create CSV header
    const headers = [
      "#",
      "Name",
      "Phone",
      "Service Chosen",
      "Message",
      "Date Submitted",
    ]

    // Map contacts to CSV rows
    const rows = filteredContacts.map((contact, index) => [
      index + 1,
      contact.user_name,
      contact.phone_number,
      contact.service_chosen,
      contact.message.replace(/(\r\n|\n|\r)/gm, " "), // remove newlines
      new Date(contact.created_at || contact.date).toLocaleString(),
    ])

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map((e) => e.map((v) => `"${v}"`).join(",")) // wrap each cell in quotes
      .join("\n")

    // Create a blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", "Contacts.csv")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Box sx={{ py: 6, background: "#f5f5f5", minHeight: "100vh" }}>
      <Container maxWidth='lg'>
        <Typography variant='h5' sx={{ mb: 3, fontWeight: 600 }}>
          Contact Submissions
        </Typography>

        {/* Search & Date Filter */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <TextField
            label='Search by name, phone, or service'
            variant='outlined'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            label='Start Date'
            type='date'
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label='End Date'
            type='date'
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleExportCSV}
              disabled={filteredContacts.length === 0}
            >
              Export to CSV
            </Button>
          </Box>
        </Box>

        {/* Loading / Error */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color='error' sx={{ textAlign: "center", mt: 6 }}>
            {error}
          </Typography>
        ) : filteredContacts.length === 0 ? (
          <Typography sx={{ textAlign: "center", mt: 6 }}>
            No contacts found.
          </Typography>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>#</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Phone</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Service Chosen</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Message</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Date Submitted</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Action</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((contact, index) => (
                      <TableRow key={contact.id || index}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{contact.user_name}</TableCell>
                        <TableCell>{contact.phone_number}</TableCell>
                        <TableCell>{contact.service_chosen}</TableCell>
                        <TableCell>{contact.message}</TableCell>
                        <TableCell>
                          {new Date(
                            contact.created_at || contact.date,
                          ).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Tooltip title='Delete Contact'>
                            <IconButton
                              color='error'
                              onClick={() => setDeleteId(contact.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component='div'
              count={filteredContacts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
          <DialogTitle>Delete Contact</DialogTitle>
          <DialogContent>
            <Alert severity='warning' sx={{ mt: 1 }}>
              Are you sure you want to permanently delete this contact? This
              action cannot be undone.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteId(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color='error' onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
      </Container>
    </Box>
  )
}

export default ContactUs
